import { Dropdown } from 'primereact/dropdown';
import React from 'react';
import Avatar from 'react-avatar';
import { Skeleton } from 'primereact/skeleton';
import styles from './Dashboard.module.scss';
import { ReactComponent as DrivingScoreIcon } from '../../assets/ic_driving_score.svg';
import { ReactComponent as EcoScoreIcon } from '../../assets/ic_eco_score.svg';
import { ReactComponent as SpeedingScoreIcon } from '../../assets/ic_speeding_score.svg';
import { ReactComponent as AccelerationScoreIcon } from '../../assets/ic_acceleration_score.svg';
import { ReactComponent as PhoneUsageScoreIcon } from '../../assets/ic_phone_usage_score.svg';
import { ReactComponent as CorneringScoreIcon } from '../../assets/ic_cornering.svg';
import { ReactComponent as BrakingScoreIcon } from '../../assets/ic_braking_score.svg';
import { ReactComponent as MileageIcon } from '../../assets/ic_mileage.svg';
import { ReactComponent as TripsCountIcon } from '../../assets/ic_trips_count.svg';
import { ReactComponent as SmileIcon } from '../../assets/ic_smile.svg';
import { ReactComponent as SadIcon } from '../../assets/ic_sad.svg';
import ScoreItem from './components/scoreItem/ScoreItem';
import RadarChart from './components/radarChart/RadarChart';
import DateRangePickerDialog from '../../components/DateRangePickerDialog';
import {
  type PeriodGroupItem,
  type PeriodItem,
  PeriodType,
  TimePeriodOptions,
} from '../../models/Period';
import Overview from './components/overview/Overview';
import type { IBreakdown } from '../../models/Dashboard';
import AppSetupCheck from '../../components/AppSetupCheck';
import { formatMileage, getPeriodEnum, noop } from '../../utils/GeneralUtils';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { useTestUsers } from '../../contexts/TestUsersContext';
import { useNotCustomerUsers } from '../../contexts/NotCustomerUsersContext';

const Dashboard: React.FC = () => {
  const tenantId = useTenantStore(selectTenantId);
  const { includeTestUsers } = useTestUsers();
  const { includeNotCustomerUsers } = useNotCustomerUsers();
  const [selectedPeriod, setSelectedPeriod] = React.useState<PeriodType>(
    PeriodType.LAST_7_DAY,
  );
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<IBreakdown>();

  React.useEffect(() => {
    const getBreakdown = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/report/breakdown', {
          params: {
            query: {
              tenantId,
              period: getPeriodEnum(selectedPeriod),
              includeTestUsers,
              includeNotCustomerUsers,
              // fromDate: DateUtils.formatToUTC(selectedDates.from),
              // toDate: DateUtils.formatToUTC(selectedDates.to),
            },
          },
        });

        if (res.data) setData(res.data as IBreakdown);
      } catch {}

      setLoading(false);
    };

    getBreakdown().catch(noop);
  }, [selectedPeriod, tenantId, includeTestUsers, includeNotCustomerUsers]);

  const [customPeriodDateDialog, setCustomPeriodDateDialog] =
    React.useState<boolean>(false);

  function onSelectedPeriodChange(event: { value: PeriodType }): void {
    setSelectedPeriod(event.value);
    // if (event.value === PeriodType.CUSTOM) setCustomPeriodDateDialog(true);
  }

  const groupedItemTemplate = (option: PeriodGroupItem): JSX.Element => {
    return (
      <div className="flex align-items-center text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  const periodItemTemplate = (option: PeriodItem): JSX.Element => {
    return (
      <div className="pl-6 text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  return (
    <div>
      <p className={styles.title}>Safety dashboard</p>

      <Overview />

      <div className={styles.timePeriodWrapper}>
        <div>
          <div className={styles.timePeriod}>Time period</div>
          <Dropdown
            options={TimePeriodOptions}
            value={selectedPeriod}
            onChange={onSelectedPeriodChange}
            optionLabel="label"
            optionGroupLabel="label"
            optionGroupChildren="items"
            placeholder="Last 7 days"
            className="p-2 min-w-[300px]"
            optionGroupTemplate={groupedItemTemplate}
            itemTemplate={periodItemTemplate}
          />
        </div>

        {/* <button className={styles.exportDataContainer}>
          <CSVIcon className={styles.csvIcon} />
          <span>Export Data</span>
        </button> */}
      </div>

      <div className={styles.scoreItemWrapper}>
        <ScoreItem
          Icon={DrivingScoreIcon}
          title="Driving Score"
          value={`${data?.score?.safetyScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={EcoScoreIcon}
          title="Eco Score"
          value={`${data?.score?.greenScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={SpeedingScoreIcon}
          title="Speeding"
          value={`${data?.score?.speedingScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={AccelerationScoreIcon}
          title="Acceleration Score"
          value={`${data?.score?.accelerationScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={PhoneUsageScoreIcon}
          title="Phone Usage Score"
          value={`${data?.score?.phoneUsageScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={CorneringScoreIcon}
          title="Cornering Score"
          value={`${data?.score?.corneringScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={BrakingScoreIcon}
          title="Braking Score"
          value={`${data?.score?.brakingScore ?? 0}`}
          loading={loading}
        />
        <ScoreItem
          Icon={MileageIcon}
          title="Mileage"
          value={formatMileage(data?.score?.mileage)}
          loading={loading}
        />
        <ScoreItem
          Icon={TripsCountIcon}
          title="Trips Count"
          value={`${data?.score?.tripsCount ?? 0}`}
          loading={loading}
        />
      </div>

      {loading ? (
        <Skeleton
          height="18rem"
          borderRadius="2.5rem"
          className="mt-[1.5rem] mb-[8rem]"
        />
      ) : (
        <div className={styles.topAndBottomDriverWrapper}>
          <div className={styles.topDriverWrapper}>
            <div className={styles.avatarWrapper}>
              <Avatar
                name={data?.bestDriver?.fullName}
                maxInitials={2}
                className={styles.topAvatar}
                size="8rem"
                round
              />

              <div className={styles.smileContainer}>
                <SmileIcon className={styles.smile} />
              </div>
            </div>

            <div className={styles.topDriverContent}>
              <span className={styles.topDriverTitle}>The top Driver</span>
              <span className={styles.topDriverName}>
                {data?.bestDriver?.fullName}
              </span>
              <span className={styles.topDriverScore}>
                Score:{' '}
                <span className={styles.topDriverScoreValue}>
                  {data?.bestDriver?.safetyScore}
                </span>
              </span>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.topDriverWrapper}>
            <div className={styles.avatarWrapper}>
              <Avatar
                name={data?.worstDriver?.fullName}
                maxInitials={2}
                className={styles.bottomAvatar}
                size="8rem"
                round
              />

              <div className={styles.smileContainer}>
                <SadIcon className={styles.smile} />
              </div>
            </div>

            <div className={styles.topDriverContent}>
              <span className={styles.bottomDriverTitle}>
                The bottom Driver
              </span>
              <span className={styles.topDriverName}>
                {data?.worstDriver?.fullName}
              </span>
              <span className={styles.topDriverScore}>
                Score:{' '}
                <span className={styles.topDriverScoreValue}>
                  {data?.worstDriver?.safetyScore}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      <RadarChart
        safetyScore={data?.score?.safetyScore ?? 0}
        phoneUsageScore={data?.score?.phoneUsageScore ?? 0}
        accelerationScore={data?.score?.accelerationScore ?? 0}
        brakesScore={data?.score?.brakingScore ?? 0}
        corneringScore={data?.score?.corneringScore ?? 0}
        speedingScore={data?.score?.speedingScore ?? 0}
        loading={loading}
      />

      <DateRangePickerDialog
        visible={customPeriodDateDialog}
        setVisible={setCustomPeriodDateDialog}
        selectedDates={null}
        onSubmit={(dates: Date[] | null) => {
          console.log('dates', dates);
        }}
      />

      <AppSetupCheck pageTitle="Dashboard" />
    </div>
  );
};

export default Dashboard;
